import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Flex, Spin } from "antd";
import { motion } from "framer-motion";

import { modules } from "../config/modules";
import ModuleCard from "../components/ModuleCard";
import type { RootState } from "../store/store";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 } as any,
  },
};

const Home: React.FC = () => {
  const { data: accessible, loading } = useSelector(
    (s: RootState) => s.accessibleModules
  );

  // Filter the launcher to only the modules whose appType matches
  // `/users/getUserAccessibleModules`. Match is case-insensitive
  // and trims whitespace to absorb minor backend differences.
  const visibleModules = useMemo(() => {
    if (!accessible) return [];
    const allowed = new Set(
      accessible
        .map((m: any) => (m?.appType ?? "").toString().toLowerCase().trim())
        .filter(Boolean)
    );
    return modules.filter((m) =>
      allowed.has(m.appType.toLowerCase().trim())
    );
  }, [accessible]);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1e293b", margin: 0 }}>
        Welcome to ProcureX
      </h1>
      <p style={{ color: "#64748b", marginTop: 4, marginBottom: 24 }}>
        Pick a module to get started.
      </p>

      {loading && !accessible ? (
        <Flex
          align="center"
          justify="center"
          style={{ minHeight: 200 }}
        >
          <Spin size="large" />
        </Flex>
      ) : visibleModules.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 32,
            textAlign: "center",
            color: "#64748b",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          You don’t have access to any modules yet. Please contact your
          administrator.
        </div>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          {visibleModules.map((m) => (
            <ModuleCard key={m.id} module={m} />
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default Home;
